import { writable } from 'svelte/store';
import type { Readable } from 'svelte/store';
import type { PlaybackError } from '~/constants/playback-errors';

interface PlaybackCapabilities {
    canEditPlaybackQueue: boolean;
}

interface InternalPlayerStateModifiers {
    setIsChromeVisible: (isVisible: boolean) => void;
    setIsNoSongLoaded: (isNoSongLoaded: boolean) => void;
    setPlaybackCapabilities: (capabilities: PlaybackCapabilities) => void;
    setPlaybackError: (playbackError: PlaybackError) => void;
    clearPlaybackError: () => void;
    setRadioSeedArtistId: (radioSeedArtistId: string) => void;
}

type ExternalPlayerModifiers = Pick<
    InternalPlayerStateModifiers,
    'setIsChromeVisible' | 'clearPlaybackError'
>;

export interface PlayerState
    extends Readable<{
            isChromeVisible: boolean;
            isNoSongLoaded: boolean;
            playbackCapabilities: PlaybackCapabilities;
            playbackError: PlaybackError | null;
            radioSeedArtistId: string | null;
        }>,
        ExternalPlayerModifiers {}

export interface PlayerStore {
    external: PlayerState;
    internal: InternalPlayerStateModifiers;
}

export const createPlayerStore = (): PlayerStore => {
    const { subscribe, update } = writable({
        isChromeVisible: false,
        isNoSongLoaded: true,
        playbackCapabilities: {} as PlaybackCapabilities,
        playbackError: null,
        radioSeedArtistId: null,
    });

    const setIsChromeVisible = (isChromeVisible: boolean) =>
        update((state) => ({
            ...state,
            isChromeVisible,
        }));

    const setIsNoSongLoaded = (isNoSongLoaded: boolean) =>
        update((state) => ({
            ...state,
            isNoSongLoaded,
        }));

    const setPlaybackCapabilities = (capabilities: PlaybackCapabilities) => {
        update((state) => ({
            ...state,
            playbackCapabilities: {
                ...state.playbackCapabilities,
                ...capabilities,
            },
        }));
    };

    const setPlaybackError = (playbackError: PlaybackError) => {
        update((state) => ({
            ...state,
            playbackError,
        }));
    };

    const clearPlaybackError = () => {
        update((state) => ({
            ...state,
            playbackError: null,
        }));
    };

    const setRadioSeedArtistId = (radioSeedArtistId: string) => {
        update((state) => ({
            ...state,
            radioSeedArtistId,
        }));
    };

    const internal = {
        setIsNoSongLoaded,
        setIsChromeVisible,
        setPlaybackCapabilities,
        setPlaybackError,
        clearPlaybackError,
        setRadioSeedArtistId,
    };

    const external = {
        subscribe,
        setIsChromeVisible,
        clearPlaybackError,
    };

    return { internal, external };
};
